import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { MarketplaceProvider } from '../types';

/**
 * Nearby care — the verified providers in an area, by category.
 *
 * The web app had no way to ask "who is there, near me": every module had its
 * own list, so finding care meant knowing which module to open first. This is
 * the single list, filtered by what kind of care it is.
 *
 * Two things this screen deliberately does not do. It does not read device
 * location and it does not show a distance, because the server computes
 * neither — the area is a *city*, and the default is the city on the
 * patient's own profile, not a guess. And a result is not a dead end: each
 * one opens the module that can actually act on it, with its live catalogue,
 * availability and booking.
 */

type Kind = 'DOCTOR' | 'LAB' | 'PHARMACY' | 'HOSPITAL' | 'IMAGING' | 'AMBULANCE';

const FILTERS: { type: Kind | null; label: string; icon: string }[] = [
  { type: null, label: 'All care', icon: '🏥' },
  { type: 'DOCTOR', label: 'Doctors', icon: '🩺' },
  { type: 'LAB', label: 'Labs', icon: '🧪' },
  { type: 'PHARMACY', label: 'Pharmacies', icon: '💊' },
  { type: 'HOSPITAL', label: 'Hospitals', icon: '🏨' },
  { type: 'IMAGING', label: 'Imaging', icon: '🩻' },
  { type: 'AMBULANCE', label: 'Ambulance', icon: '🚑' },
];

const KIND_LABEL: Record<string, string> = {
  DOCTOR: 'Doctor', LAB: 'Lab', PHARMACY: 'Pharmacy',
  HOSPITAL: 'Hospital', IMAGING: 'Imaging centre', AMBULANCE: 'Ambulance service',
};

/**
 * One colour per kind of care, the same vocabulary the app uses, so a
 * pharmacy is the same green in both places and the trust mark is never a
 * generic tick that means nothing in particular.
 */
const ROLE_COLOR: Record<string, string> = {
  DOCTOR: '#2563EB', HOSPITAL: '#0B2D4D', PHARMACY: '#059669',
  LAB: '#0891B2', IMAGING: '#7C3AED', AMBULANCE: '#F0553C',
};

const roleColor = (type: string) => ROLE_COLOR[type] || T.textSec;

/** The glyph the app puts beside each kind. */
const ROLE_PATH: Record<string, string> = {
  DOCTOR: 'M12 11a4 4 0 100-8 4 4 0 000 8z M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2',
  HOSPITAL: 'M3 21h18 M5 21V7l7-4 7 4v14 M9 12h6 M12 9v6',
  PHARMACY: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
  LAB: 'M9 3v6l-5 9a2 2 0 002 3h12a2 2 0 002-3l-5-9V3 M8 3h8',
  IMAGING: 'M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2 M7 13h2l2-4 2 8 2-4h2',
  AMBULANCE: 'M3 17V7a1 1 0 011-1h10v11H3z M14 10h4l3 3v4h-7v-7z M7 20a2 2 0 100-4 2 2 0 000 4z M17 20a2 2 0 100-4 2 2 0 000 4z',
};

function RoleIcon({ type }: { type: string }) {
  const color = roleColor(type);
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 12, background: `${color}14`,
      display: 'grid', placeItems: 'center', flexShrink: 0,
    }}>
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d={ROLE_PATH[type] || 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'} />
      </svg>
    </div>
  );
}

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 14, boxShadow: T.shadow, fontFamily: T.font,
};

/**
 * Where a result goes when it is opened. Doctors, pharmacies, hospitals and
 * imaging centres are the same records the care-services modules list, so
 * they open on that provider. Labs are a separate catalogue with its own ids,
 * and ambulance dispatch is not per-provider, so those open their module —
 * sending a marketplace id into a screen that keys by something else would
 * only produce a "not found".
 */
function destination(p: MarketplaceProvider): { to: string; label: string } {
  const key = p.slug || p.id;
  switch (p.type) {
    case 'DOCTOR': return { to: `/patient/care-services/doctors/${key}`, label: 'View & book' };
    case 'PHARMACY': return { to: `/patient/care-services/pharmacy/${key}`, label: 'Open store' };
    case 'HOSPITAL': return { to: `/patient/care-services/hospitals/${key}`, label: 'View & book OPD' };
    case 'IMAGING': return { to: `/patient/care-services/imaging/${key}`, label: 'View & book scan' };
    case 'LAB': return { to: '/patient/care-services/labs', label: 'Open labs' };
    case 'AMBULANCE': return { to: '/patient/care-services/ambulance', label: 'Request an ambulance' };
    default: return { to: '/patient/care-services', label: 'Open care services' };
  }
}

export default function NearbyCare() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const initialType = (params.get('type') || '').toUpperCase();
  const [type, setType] = useState<Kind | null>(
    FILTERS.some((f) => f.type === initialType) ? (initialType as Kind) : null,
  );
  /** null = every city. That is the honest state when no city is recorded. */
  const [city, setCity] = useState<string | null>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [rows, setRows] = useState<MarketplaceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [areaReady, setAreaReady] = useState(false);
  const [error, setError] = useState('');

  // The area picker offers only cities that actually have providers, so a
  // choice here always means something rather than silently matching nothing.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await patientApi.getMarketplaceProviders({ limit: 100 });
        const names = [...new Set(
          all.providers.map((p) => (p.city || '').trim()).filter(Boolean),
        )].sort();
        let home: string | null = null;
        try {
          const profile = await patientApi.getProfile();
          const c = (profile.city || '').trim();
          if (c) home = names.find((n) => n.toLowerCase() === c.toLowerCase()) ?? null;
        } catch {
          // No profile city is a normal state; the list simply stays at every city.
        }
        if (!alive) return;
        setCities(names);
        setCity(home);
      } catch {
        if (alive) setCities([]);
      } finally {
        if (alive) setAreaReady(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await patientApi.getMarketplaceProviders({
        ...(type ? { type } : {}),
        ...(city ? { city } : {}),
        limit: 100,
      });
      setRows(r.providers);
    } catch (e: any) {
      setError(e?.body?.message || 'Providers could not be loaded — check your connection.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [type, city]);

  // Wait for the area to resolve, otherwise the first load runs against
  // "every city" and is immediately replaced — two lists in one second.
  useEffect(() => { if (areaReady) void load(); }, [areaReady, load]);

  const choose = (t: Kind | null) => {
    setType(t);
    const next = new URLSearchParams(params);
    if (t) next.set('type', t); else next.delete('type');
    setParams(next, { replace: true });
  };

  const verified = useMemo(() => rows.filter((r) => r.isVerified).length, [rows]);

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>Nearby care</h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          Providers on the network in your area. Distances are not shown because none are
          measured — the area here is a city, not your exact location.
        </p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {FILTERS.map((f) => {
          const on = f.type === type;
          return (
            <button
              key={f.label}
              onClick={() => choose(f.type)}
              style={{
                padding: '8px 13px', borderRadius: 20, cursor: 'pointer',
                fontSize: 11, fontWeight: 700, fontFamily: T.font,
                color: on ? T.white : T.textSec,
                background: on ? T.teal : T.white,
                border: `1px solid ${on ? T.teal : T.border}`,
              }}
            >
              {f.icon} {f.label}
            </button>
          );
        })}
      </div>

      <div style={{ ...card, padding: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label htmlFor="nearby-city" style={{ fontSize: 11, fontWeight: 700, color: T.textSec }}>Area</label>
        <select
          id="nearby-city"
          value={city ?? ''}
          onChange={(e) => setCity(e.target.value || null)}
          style={{
            padding: '8px 10px', borderRadius: 10, border: `1px solid ${T.border}`,
            fontSize: 12, fontFamily: T.font, color: T.text, background: T.white, minWidth: 160,
          }}
        >
          <option value="">Every city</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {!loading && (
          <span style={{ fontSize: 10.5, color: T.textTer }}>
            {rows.length} listed{verified > 0 ? ` · ${verified} verified by NHC` : ''}
          </span>
        )}
      </div>

      {error && (
        <div style={{ ...card, borderColor: `${T.danger}55`, padding: 12, fontSize: 12, color: T.danger }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Looking for care near you…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Nothing here yet</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            {city
              ? `No ${type ? KIND_LABEL[type].toLowerCase() : 'provider'} has joined the network in ${city} so far.`
              : 'No provider of this kind is on the network yet.'}
          </div>
          {city && (
            <button
              onClick={() => setCity(null)}
              style={{
                marginTop: 12, padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border}`,
                background: T.white, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                color: T.teal, fontFamily: T.font,
              }}
            >
              Look in every city
            </button>
          )}
        </div>
      ) : (
        rows.map((p) => {
          const go = destination(p);
          return (
            <div key={p.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <RoleIcon type={p.type} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
                    {p.name}
                    {/* Role-coloured, like the app's badge: the mark says WHAT
                        was verified, not just that something was. */}
                    {p.isVerified && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700, color: T.white,
                        background: roleColor(p.type), borderRadius: 6, padding: '2px 6px', verticalAlign: 'middle',
                      }}>
                        Verified
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
                    {[
                      KIND_LABEL[p.type] || p.type,
                      p.specialties.filter(Boolean).join(', ') || null,
                    ].filter(Boolean).join(' · ')}
                  </div>
                  {(p.address || p.city) && (
                    <div style={{ fontSize: 11, color: T.textSec, marginTop: 6, lineHeight: 1.5 }}>
                      {[p.address, p.city, p.pincode].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {/* A rating with nobody behind it is noise, so it appears
                      only once someone has actually rated this provider. */}
                  {p.reviewCount > 0 && (
                    <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 4 }}>
                      ★ {p.rating.toFixed(1)} · {p.reviewCount} {p.reviewCount === 1 ? 'review' : 'reviews'}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => navigate(go.to)}
                  style={{
                    padding: '9px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 700, color: T.white, background: T.teal,
                    fontFamily: T.font, whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {go.label}
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
